/**
 * What waits for the admin team in each area. The counts the admin overview and the admin
 * navigation show, read from the same lists as the areas' own pages.
 */

import { useSession } from "../auth/session.js";
import { useAdminIssues } from "../campaigns/issues.js";
import { useAllRequests } from "../campaigns/requests.js";
import { useAdminCampaigns } from "./campaigns.js";
import { useDeployments } from "./deployments.js";
import { useReviews } from "./reviews.js";
import { useSettlements } from "./settlements.js";
import { isNewWallet, useAdminWallets, useWalletsSeenUntil } from "./wallets.js";

/** Each count is undefined until its list has loaded. */
export interface AdminQueues {
  /** Requests waiting for review. */
  requests: number | undefined;
  /** Changes of accepted or lapsed requests waiting for review (D25). */
  changes: number | undefined;
  /** Deposits the owner must void. */
  toVoid: number | undefined;
  /** Checks waiting for a person. */
  reviews: number | undefined;
  /** Issues not yet decided. */
  issues: number | undefined;
  /** Settlements not yet executed or withdrawn. */
  settlements: number | undefined;
  /** Wallets that first signed in since this admin last opened the wallets' page. */
  newWallets: number | undefined;
  /** Whether a registered deployment receives new campaigns. */
  receiving: boolean | undefined;
  /** Whether the escrow's owner paused campaign creation on the receiving deployment. */
  paused: boolean | undefined;
  /** Whether any list is still loading. */
  loading: boolean;
  /** The areas, by address, whose list could not be read. Nothing is known of what waits. */
  failed: ReadonlySet<string>;
}

export function useAdminQueues(): AdminQueues {
  const requests = useAllRequests("submitted");
  const changes = useAllRequests("change");
  const toVoid = useAdminCampaigns(true);
  const reviews = useReviews();
  const issues = useAdminIssues("open");
  const settlements = useSettlements("open");
  const deployments = useDeployments();
  const wallets = useAdminWallets();
  const seenUntil = useWalletsSeenUntil(useSession().data?.wallet);
  const lists = {
    "/admin/campaign-requests": requests,
    "/admin/campaign-requests?changes": changes,
    "/admin/deployments": deployments,
    "/admin/campaigns": toVoid,
    "/admin/reviews": reviews,
    "/admin/issues": issues,
    "/admin/settlements": settlements,
    "/admin/wallets": wallets,
  };
  return {
    requests: requests.data?.length,
    changes: changes.data?.length,
    toVoid: toVoid.data?.length,
    reviews: reviews.data?.length,
    issues: issues.data?.length,
    settlements: settlements.data?.length,
    newWallets: wallets.data?.wallets.filter((wallet) => isNewWallet(wallet, seenUntil)).length,
    receiving: deployments.data?.some((deployment) => deployment.acceptsNewCampaigns),
    paused: deployments.data?.some(
      (deployment) => deployment.acceptsNewCampaigns && deployment.creationPaused,
    ),
    loading: Object.values(lists).some((query) => query.isPending),
    failed: new Set(
      Object.entries(lists)
        .filter(([, query]) => query.isError)
        // Both lists of requests belong to the requests' area.
        .map(([to]) => to.split("?")[0] ?? to),
    ),
  };
}

/** An admin area. Its page, its name, what it is for, and what waits in it. */
export interface AdminArea {
  to: string;
  label: string;
  title: string;
  text: string;
  /**
   * What waits in the area, such as "2 waiting for review". Null when nothing does. `news` marks
   * something to know rather than something to do, such as new wallets. It shows in the brand's
   * blue, and the overview does not count it as work.
   */
  waiting: (queues: AdminQueues) => { count: number; text: string; news?: boolean } | null;
}

function waiting(count: number | undefined, text: string) {
  return count ? { count, text: `${count} ${text}` } : null;
}

/** The admin areas, in the order of the navigation. */
export const ADMIN_AREAS: AdminArea[] = [
  {
    to: "/admin/campaign-requests",
    label: "Requests",
    title: "Campaign requests",
    text: "Review brands' requests and the changes they ask for to terms, brief and contact.",
    waiting: (queues) => {
      const parts = [
        queues.requests ? `${queues.requests} waiting for review` : null,
        queues.changes
          ? `${queues.changes} ${queues.changes === 1 ? "change" : "changes"} to review`
          : null,
      ].filter((part) => part !== null);
      const count = (queues.requests ?? 0) + (queues.changes ?? 0);
      return count > 0 ? { count, text: parts.join(", ") } : null;
    },
  },
  {
    to: "/admin/deployments",
    label: "Deployments",
    title: "Escrow deployments",
    text: "Register escrow deployments and choose the one that receives new campaigns.",
    waiting: (queues) =>
      queues.receiving === false
        ? { count: 1, text: "No deployment receives new campaigns" }
        : queues.paused
          ? { count: 1, text: "Campaign creation is paused on-chain" }
          : null,
  },
  {
    to: "/admin/campaigns",
    label: "Campaigns",
    title: "Campaigns",
    text: "Deposits read from the chain, and those the owner must void.",
    waiting: (queues) =>
      waiting(queues.toVoid, queues.toVoid === 1 ? "deposit to void" : "deposits to void"),
  },
  {
    to: "/admin/reviews",
    label: "Reviews",
    title: "Reviews",
    text: "Checks Morcast cannot decide alone, to pass or fail with a note.",
    waiting: (queues) =>
      waiting(queues.reviews, queues.reviews === 1 ? "check waiting" : "checks waiting"),
  },
  {
    to: "/admin/issues",
    label: "Issues",
    title: "Issues",
    text: "Issues raised in results' reviews, to uphold or reject, correcting a check if needed.",
    waiting: (queues) => waiting(queues.issues, queues.issues === 1 ? "open issue" : "open issues"),
  },
  {
    to: "/admin/settlements",
    label: "Settlements",
    title: "Settlements",
    text: "Each campaign's settle call, sent from the settler wallet, and the fees to withdraw.",
    waiting: (queues) =>
      waiting(
        queues.settlements,
        queues.settlements === 1 ? "open settlement" : "open settlements",
      ),
  },
  {
    to: "/admin/wallets",
    label: "Wallets",
    title: "Wallets",
    text: "Every wallet that signed in, and what it has done on Morcast so far.",
    waiting: (queues) =>
      queues.newWallets
        ? {
            count: queues.newWallets,
            text: `${queues.newWallets} new ${queues.newWallets === 1 ? "wallet" : "wallets"}`,
            news: true,
          }
        : null,
  },
  {
    to: "/admin/announcements",
    label: "Announcements",
    title: "Announcements",
    text: "Tell everyone, brands or creators something, among their notifications.",
    // Nothing waits here. The team writes an announcement when it has one.
    waiting: () => null,
  },
];
